import React, { useMemo, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useBattleCardsStore } from 'store/battlecards.store';
import { useProjectTreeStore } from 'store/projectContract.store';
import { useVersionStore } from 'store/version.store';

import { QueryParams, useMutationCreateSegment } from 'api/mutations/createSegment.mutation';
import { useMutationUpdateSegment } from 'api/mutations/updateSegment.mutation';
import { useQueryGetSegmentsList } from 'api/queries/getSegmentsList.query';

import { RadioComponent } from 'components/common/RadioComponent/radioComponent';
import TextSearchInput from 'components/common/TextSearchInput/textSearchInput';

import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/components/ActivitiesSegments/components/SaveSegment/SaveSegment.module.scss';
import { generateFilterString } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/components/ActivitiesSegments/components/SaveSegment/SaveSegment.utils';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import {
    ConfirmSaveModal,
    CustomizedButton,
    CustomizedFormErrorMessage,
    CustomizedModalBase,
    MyCustomSelect,
} from 'components/common';

type FormProps = QueryParams;

interface Props {
    isShowModal: boolean;
    handleModalClose: () => void;
}

export const SaveSegment = ({ isShowModal, handleModalClose }: Props) => {
    const { widgetFiltersData, filterData } = useBattleCardsStore();
    const { contract } = useProjectTreeStore();
    const { version } = useVersionStore();
    const { pathname, search } = useLocation();
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();
    const { sendEventWithDimensions } = useEventWithDimensions();
    const [confirmModal, setConfirmModal] = useState<boolean>(false);
    const [radioOption, setRadioOption] = useState<'create' | 'override'>('create');

    const { mutate: createSegment } = useMutationCreateSegment();
    const { mutate: updateSegment } = useMutationUpdateSegment();

    const { data: segments } = useQueryGetSegmentsList({ metaDataId: version?.id, contractId: contract?.id });

    const {
        watch,
        handleSubmit,
        control,
        formState: { errors },
        reset,
        setValue,
    } = useForm<FormProps>({
        values: {
            segmentId: null,
            contractId: contract?.id,
            entireProject: true,
            filter: {
                filters: filterData.filter.filters,
            },
            name: generateFilterString(widgetFiltersData),
            metaDataId: version?.id,
            userGenerated: true,
            url: `${pathname.substring(1)}${search}`,
        },
    });

    const handleSave = () => {
        const payload = watch();
        const mutation = radioOption === 'create' ? createSegment : updateSegment;
        sendEventWithDimensions({ action: 'Save Segment', label: payload.name, category: 'Project Activities' });
        mutation(payload, {
            onSuccess: () => {
                queryClient.invalidateQueries('getSegmentsList').then(() => {
                    handleSnackBar(`Segment ${radioOption === 'create' ? 'created' : 'updated'}`, 'success');
                });
            },
            onError: (error) => {
                const message = 'Error saving segment';
                DEFAULT_ERROR({ error, message, handleError: handleSnackBar });
            },
            onSettled: () => {
                closeModal();
            },
        });
    };

    const closeModal = () => {
        handleModalClose();
        reset();
    };

    const options = useMemo(() => {
        return (
            segments?.map((item) => ({
                label: item.name,
                value: item.segmentId,
            })) || []
        );
    }, [segments]);

    return (
        <>
            <CustomizedModalBase isModalOpen={isShowModal} handleModalClose={closeModal}>
                <div className={classes.saveSegment}>
                    <form onSubmit={handleSubmit(() => setConfirmModal(true))}>
                        <div className={classes.title}>Save current filter as Segment</div>

                        {widgetFiltersData.length > 0 && (
                            <div className={classes.widgetFiltersWrapper}>
                                <div className={classes.widgetFiltersTitle}>Applied Filter:</div>
                                <div className={classes.widgetFiltersContent}>
                                    <PinboardWidgetPills
                                        key={'widgetFilters'}
                                        widgetId={'widgetId'}
                                        filters={widgetFiltersData}
                                    />
                                </div>
                            </div>
                        )}

                        <div className={classes.optionContainer}>
                            <label>
                                <RadioComponent
                                    onChange={() => {
                                        setRadioOption('create');
                                        setValue('name', '');
                                    }}
                                    value={'create'}
                                    checked={radioOption === 'create'}
                                    name={'create'}
                                />
                                Create Segment
                            </label>

                            <label>
                                <RadioComponent
                                    onChange={() => setRadioOption('override')}
                                    value={'override'}
                                    checked={radioOption === 'override'}
                                    name={'create'}
                                />
                                Override Existing Segment
                            </label>
                        </div>
                        <div className={classes.selectTitle}>Enter segment name</div>
                        {radioOption === 'create' && (
                            <div className={classes.selectWrapper}>
                                <Controller
                                    render={({ field }) => (
                                        <TextSearchInput
                                            {...field}
                                            placeholder={'Segment name...'}
                                            isSearch={false}
                                            maxLength={30}
                                            handleClear={() => {
                                                setValue('name', '');
                                            }}
                                        />
                                    )}
                                    name="name"
                                    control={control}
                                    rules={{
                                        validate: {
                                            required: (value) => value.length > 0 || 'Must select at least one tag',
                                        },
                                    }}
                                />
                                {errors.name && <CustomizedFormErrorMessage text={errors.name.message} />}
                            </div>
                        )}

                        {radioOption === 'override' && (
                            <div className={classes.selectWrapper}>
                                <Controller
                                    render={({ field }) => (
                                        <MyCustomSelect<ISelectOption<number>>
                                            {...field}
                                            value={options.find((item) => item.value === watch('segmentId')) || null}
                                            onChange={(value) => {
                                                if (value) {
                                                    field.onChange(value.value);
                                                    setValue('name', value.label);
                                                } else {
                                                    field.onChange(null);
                                                    setValue('name', '');
                                                }
                                            }}
                                            options={options}
                                            isClearable={true}
                                            placeholder={'Select segment...'}
                                        />
                                    )}
                                    name="segmentId"
                                    control={control}
                                />
                            </div>
                        )}

                        <div className={classes.footer}>
                            <div className={classes.container}>
                                <CustomizedButton size={'large'} color={'secondary'} onClick={closeModal}>
                                    Cancel
                                </CustomizedButton>

                                <CustomizedButton size={'large'} color={'primary'} type={'submit'}>
                                    Save
                                </CustomizedButton>
                            </div>
                        </div>
                    </form>
                </div>
            </CustomizedModalBase>

            <ConfirmSaveModal
                isShowModal={confirmModal}
                onHideModal={() => setConfirmModal(false)}
                onConfirm={() => {
                    setConfirmModal(false);
                    handleSave();
                }}
                actionButtonLabel={'Yes'}
            >
                Are you sure you want to save this segment?
            </ConfirmSaveModal>
        </>
    );
};
